/**
 * Wecare Best Health logo
 *
 * Concept: two embracing leaf-petals form a stylised "W" silhouette,
 * with a small heart-seed at the convergence point. Reads as
 * "care + growth + the W initial" without resorting to medical clichés.
 *
 * Variants:
 *   - "mark"     → square badge only (favicon, avatars, buttons)
 *   - "full"     → mark + wordmark (header, footer, hero)
 *   - "wordmark" → text-only
 *
 * Tones (controlled via tone prop):
 *   - "brand"    → teal→green gradient on a brand-coloured plate
 *   - "onBrand"  → white mark on transparent (use over coloured backgrounds)
 *   - "ink"      → solid foreground colour (good for monochrome / print)
 */

import { SITE } from "@/lib/site";

// Heart-seed used at the convergence point of the two leaves.
const HEART_PATH =
  "M32 41.4c-3.4-2.7-7.6-5.3-7.6-9.3 0-2.4 1.9-4.1 4.2-4.1 1.6 0 2.7.8 3.4 2 .7-1.2 1.8-2 3.4-2 2.3 0 4.2 1.7 4.2 4.1 0 4-4.2 6.6-7.6 9.3z";

// Two leaf paths that meet centrally to form a W silhouette.
// Each leaf is a soft, asymmetric petal anchored from the badge corner toward the centre-bottom.
const LEAF_LEFT_PATH =
  "M14 17c-1.6 1-2 3-1.3 4.7l9.6 22.4c1 2.4 4.5 2.6 5.8.3l4.7-8.5c.7-1.3.7-3 0-4.3l-7.3-13.6c-.6-1.1-1.7-1.8-3-1.9l-7.3-.4c-.4 0-.9.1-1.2.3z";
const LEAF_RIGHT_PATH =
  "M50 17c1.6 1 2 3 1.3 4.7l-9.6 22.4c-1 2.4-4.5 2.6-5.8.3l-4.7-8.5c-.7-1.3-.7-3 0-4.3l7.3-13.6c.6-1.1 1.7-1.8 3-1.9l7.3-.4c.4 0 .9.1 1.2.3z";

function Mark({ tone = "brand", className = "", size = 40 }) {
  const id = "wbh-grad";
  const useGradient = tone === "brand";
  const fillLeaves = tone === "ink" ? "currentColor" : "#ffffff";
  const accent = tone === "ink" ? "currentColor" : "#ffffff";

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Wecare Best Health"
      className={className}
    >
      {useGradient && (
        <defs>
          <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(188 78% 32%)" />
            <stop offset="55%" stopColor="hsl(178 70% 32%)" />
            <stop offset="100%" stopColor="hsl(150 55% 38%)" />
          </linearGradient>
          <linearGradient id={`${id}-shine`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>
      )}

      {/* Background plate */}
      {useGradient ? (
        <>
          <rect x="0" y="0" width="64" height="64" rx="16" fill={`url(#${id})`} />
          <rect x="0" y="0" width="64" height="32" rx="16" fill={`url(#${id}-shine)`} />
        </>
      ) : (
        tone === "onBrand" && (
          <rect x="0" y="0" width="64" height="64" rx="16" fill="rgba(255,255,255,0.0)" />
        )
      )}

      {/* Two leaf-petals forming the W */}
      <g>
        <path d={LEAF_LEFT_PATH} fill={fillLeaves} opacity={tone === "ink" ? 1 : 0.96} />
        <path d={LEAF_RIGHT_PATH} fill={fillLeaves} opacity={tone === "ink" ? 1 : 0.96} />
        {/* Heart-seed at convergence */}
        <path d={HEART_PATH} fill={accent} />
      </g>
    </svg>
  );
}

function Wordmark({ size = "md", tone = "ink", className = "" }) {
  const sizes = {
    sm: { primary: "text-[14px] leading-tight", secondary: "text-[9px] tracking-[0.22em]" },
    md: { primary: "text-base leading-tight", secondary: "text-[10px] tracking-[0.2em]" },
    lg: { primary: "text-xl leading-tight", secondary: "text-[11px] tracking-[0.22em]" },
  };
  const s = sizes[size] || sizes.md;
  const colorPrimary = tone === "onBrand" ? "text-white" : "text-foreground";
  const colorSecondary = tone === "onBrand" ? "text-white/70" : "text-muted-foreground";

  return (
    <div className={`leading-tight ${className}`}>
      <div className={`font-display tracking-tight ${s.primary} ${colorPrimary}`}>
        {SITE.short}
      </div>
      <div className={`uppercase ${s.secondary} ${colorSecondary}`}>
        Psychiatric Care · IL
      </div>
    </div>
  );
}

export default function Logo({
  variant = "full",
  tone = "brand",
  size = 40,
  wordmarkSize = "md",
  wordmarkTone,
  className = "",
}) {
  const wmTone = wordmarkTone || (tone === "onBrand" ? "onBrand" : "ink");

  if (variant === "wordmark") {
    return <Wordmark size={wordmarkSize} tone={wmTone} className={className} />;
  }

  if (variant === "mark") {
    return <Mark tone={tone} size={size} className={className} />;
  }

  // full
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <Mark tone={tone} size={size} />
      <Wordmark size={wordmarkSize} tone={wmTone} />
    </div>
  );
}

export { Mark, Wordmark };
